import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useRadioGroupOptions } from '@alltrails/denali/components/RadioGroup';
import { RadioFilterConfig } from '@alltrails/denali/src/components/FilterBar';
import type TrailActivityType from '@alltrails/trails/types/TrailActivityType';
import getTrailActivityTypeLabel from '@alltrails/trails/utils/getTrailActivityTypeLabel';
import ExploreServiceFilters from '../../types/ExploreServiceFilters';

const all = 'all' as const;

type OptionValue = typeof all | TrailActivityType;

const activityTypes: OptionValue[] = [
  all,
  'hiking',
  'mountain-biking',
  'trail-running',
  'running',
  'road-biking',
  'backpacking',
  'walking',
  'off-road-driving',
  'scenic-driving',
  'bike-touring',
  'snowshoeing',
  'cross-country-skiing',
  'skiing',
  'paddle-sports',
  'camping',
  'fishing',
  'birding',
  'horseback-riding',
  'rock-climbing',
  'via-ferrata'
];

export const isValidActivity = (activity?: string): activity is TrailActivityType =>
  activity !== all && activityTypes.includes(activity as OptionValue);

const useActivityFilterConfig = (
  exploreServiceFilters: ExploreServiceFilters,
  onFiltersChange: (updatedFilters: ExploreServiceFilters) => void
): RadioFilterConfig => {
  const intl = useIntl();

  const selected: OptionValue = exploreServiceFilters.activity?.length ? exploreServiceFilters.activity[0] : all;
  const { onChange, options, clearOptions, selectedOption } = useRadioGroupOptions(
    activityTypes.map(activityType => ({
      selected: activityType === selected,
      text: activityType === all ? intl.formatMessage({ defaultMessage: 'All' }) : getTrailActivityTypeLabel(intl, activityType),
      value: activityType
    })),
    all
  );

  useEffect(() => {
    if (selected !== selectedOption?.value) {
      onChange(selected);
    }
    // Only want to update when we get a new value
  }, [selected]);

  useEffect(() => {
    const typedValue = (selectedOption?.value ?? all) as OptionValue;
    const newFilters = { ...exploreServiceFilters };
    delete newFilters.activity;
    if (typedValue === all) {
      // Do nothing, act like there is no activity filter
    } else {
      newFilters.activity = [typedValue];
    }
    onFiltersChange(newFilters);

    // Only want to trigger this the selected value changes
  }, [selectedOption?.value]);

  return {
    type: 'radio',
    id: 'activity',
    filterChip: {
      onClear: clearOptions,
      selectedItems: selectedOption && selectedOption.value !== all ? [selectedOption.text] : undefined,
      unselectedText: intl.formatMessage({ defaultMessage: 'Activity' })
    },
    radioGroup: {
      labelText: intl.formatMessage({ defaultMessage: 'Activity' }),
      name: 'activity-type',
      onChange,
      options
    }
  };
};

export default useActivityFilterConfig;
